import { useMemo } from "react";

export const useAvatarVoiceReply = ({ enabled, language }) => {
  const speakReply = (text, opts = {}) => {
    try {
      if (!text || !window.speechSynthesis) return;
      const utter = new SpeechSynthesisUtterance(text);
      const languageMappings = {
        en: "en-US",
        "en-GB": "en-GB",
        "en-US": "en-US",
        es: "es-ES",
        pt: "pt-PT",
        de: "de-DE",
        fr: "fr-FR",
        hi: "hi-IN",
        it: "it-IT",
        ja: "ja-JP",
      };
      const currentLang = language || "en";
      const desiredLang = languageMappings[currentLang] || "en-US";
      utter.lang = desiredLang;
      utter.volume = 1;
      utter.rate = 1;
      utter.pitch = 1;

      const pickVoice = (voices, targetLang) => {
        if (!Array.isArray(voices) || !voices.length) return null;
        const exact = voices.find((v) => v.lang && v.lang.toLowerCase() === targetLang.toLowerCase());
        if (exact) return exact;
        const prefix = targetLang.split("-")[0].toLowerCase();
        const byPrefix = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(prefix));
        return byPrefix || voices[0];
      };

      const trySpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        const voice = pickVoice(voices, utter.lang);
        if (voice) utter.voice = voice;
        if (typeof opts.onstart === "function") utter.onstart = opts.onstart;
        if (typeof opts.onend === "function") utter.onend = opts.onend;
        if (typeof opts.onerror === "function") utter.onerror = opts.onerror;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      };

      const voices = window.speechSynthesis.getVoices();
      if (!voices || !voices.length) {
        const handler = () => {
          trySpeak();
          window.speechSynthesis.removeEventListener("voiceschanged", handler);
        };
        window.speechSynthesis.addEventListener("voiceschanged", handler);
        setTimeout(() => {
          trySpeak();
          window.speechSynthesis.removeEventListener("voiceschanged", handler);
        }, 500);
      } else {
        trySpeak();
      }
    } catch (e) {}
  };

  const buildVoiceReply = (parsedResult, execResult) => {
    const type = parsedResult?.type;
    const studentName = parsedResult?.studentName;
    const lessonName = parsedResult?.lessonName;
    const elementCode = parsedResult?.elementCode;
    const msg = String(execResult?.message || "");
    const lcMsg = msg.toLowerCase();
    const langBase = String(language || "en").toLowerCase().split("-")[0];
    const say = (en, overrides) => {
      const o =
        typeof overrides === "string"
          ? { es: overrides }
          : overrides || {};
      const map = {
        en,
        es: o.es || en,
        pt: o.pt || en,
        fr: o.fr || en,
        de: o.de || en,
        hi: o.hi || en,
        it: o.it || en,
        ja: o.ja || en,
      };
      return map[langBase] || en;
    };
    const original = String(parsedResult?.originalCommand || "").toLowerCase();
    const saidDraw = /(draw|dibuja|dibujar|desenhar|dessine|zeichnen|disegna|描いて|描く|बनाओ|बनाएं)/i.test(original);
    const hasRect = /(rectangle|rectangulo|rectángulo|cuadrado|retângulo|rettangolo|quadrato|rechteck|長方形|四角形|正方形|आयत|आयताकार|वर्ग)/i.test(original);
    const hasEllipse = /(ellipse|circle|círculo|circulo|elipse|cercle|kreis|cerchio|ellisse|円|楕円|वृत्त|गोल|अंडाकार)/i.test(original);
    const hasLine = /(line|línea|linea|linha|ligne|linie|線|रेखा)/i.test(original);
    const hasArrow = /(arrow|flecha|pfeil|freccia|矢印)/i.test(original);
    const hasText = /(text|texto|texte|testo|पाठ|テキスト)/i.test(original);
    const hasFormula = /(formula|equation|fórmula|ecuación|équation|gleichung|formula|equazione|式|方程式|latex)/i.test(original);

    switch (type) {
      case "presentAll":
        return say("I will mark all students present.", {
          es: "Marcaré a todos los estudiantes presentes.",
          pt: "Vou marcar todos os alunos como presentes.",
          fr: "Je vais marquer tous les élèves présents.",
          de: "Ich werde alle Schüler als anwesend markieren.",
          hi: "मैं सभी छात्रों को उपस्थित चिह्नित करूँगा.",
          it: "Segnerò tutti gli studenti presenti.",
          ja: "すべての生徒を出席にします。",
        });
      case "absentAll":
        return say("I will mark all students absent.", {
          es: "Marcaré a todos los estudiantes ausentes.",
          pt: "Vou marcar todos os alunos como ausentes.",
          fr: "Je vais marquer tous les élèves absents.",
          de: "Ich werde alle Schüler als abwesend markieren.",
          hi: "मैं सभी छात्रों को अनुपस्थित चिह्नित करूँगा.",
          it: "Segnerò tutti gli studenti assenti.",
          ja: "すべての生徒を欠席にします。",
        });
      case "lateAll":
        return say("I will mark all students late.", {
          es: "Marcaré a todos los estudiantes como tarde.",
          pt: "Vou marcar todos os alunos como atrasados.",
          fr: "Je vais marquer tous les élèves en retard.",
          de: "Ich werde alle Schüler als zu spät markieren.",
          hi: "मैं सभी छात्रों को देर से चिह्नित करूँगा.",
          it: "Segnerò tutti gli studenti in ritardo.",
          ja: "すべての生徒を遅刻にします。",
        });
      case "present":
        return say(`I will mark ${studentName} present.`, {
          es: `Marcaré a ${studentName} presente.`,
          pt: `Vou marcar ${studentName} como presente.`,
          fr: `Je vais marquer ${studentName} présent.`,
          de: `Ich werde ${studentName} als anwesend markieren.`,
          hi: `मैं ${studentName} को उपस्थित चिह्नित करूँगा.`,
          it: `Segnerò ${studentName} presente.`,
          ja: `${studentName} を出席にします。`,
        });
      case "absent":
        return say(`I will mark ${studentName} absent.`, {
          es: `Marcaré a ${studentName} ausente.`,
          pt: `Vou marcar ${studentName} como ausente.`,
          fr: `Je vais marquer ${studentName} absent.`,
          de: `Ich werde ${studentName} als abwesend markieren.`,
          hi: `मैं ${studentName} को अनुपस्थित चिह्नित करूँगा.`,
          it: `Segnerò ${studentName} assente.`,
          ja: `${studentName} を欠席にします。`,
        });
      case "late":
        return say(`I will mark ${studentName} late.`, {
          es: `Marcaré a ${studentName} como tarde.`,
          pt: `Vou marcar ${studentName} como atrasado.`,
          fr: `Je vais marquer ${studentName} en retard.`,
          de: `Ich werde ${studentName} als zu spät markieren.`,
          hi: `मैं ${studentName} को देर से चिह्नित करूँगा.`,
          it: `Segnerò ${studentName} in ritardo.`,
          ja: `${studentName} を遅刻にします。`,
        });
      case "navigateLesson":
      case "goToLesson":
        return lessonName
          ? say(`Navigating to ${lessonName}.`, {
              es: `Navegando a ${lessonName}.`,
              pt: `Navegando para ${lessonName}.`,
              fr: `Navigation vers ${lessonName}.`,
              de: `Navigiere zu ${lessonName}.`,
              hi: `${lessonName} पर जा रहा हूँ.`,
              it: `Sto navigando verso ${lessonName}.`,
              ja: `${lessonName} に移動しています。`,
            })
          : say("Navigating to lesson.", {
              es: "Navegando a la lección.",
              pt: "Navegando para a aula.",
              fr: "Navigation vers la leçon.",
              de: "Zur Lektion navigieren.",
              hi: "पाठ पर जा रहा हूँ.",
              it: "Sto navigando alla lezione.",
              ja: "レッスンに移動しています。",
            });
      case "nextLesson":
        return say("Moving to the next lesson.", {
          es: "Pasando a la siguiente lección.",
          pt: "Indo para a próxima aula.",
          fr: "Passage à la leçon suivante.",
          de: "Zur nächsten Lektion wechseln.",
          hi: "अगले पाठ पर जा रहा हूँ.",
          it: "Passo alla lezione successiva.",
          ja: "次のレッスンに移動します。",
        });
      case "previousLesson":
        return say("Going back to the previous lesson.", {
          es: "Volviendo a la lección anterior.",
          pt: "Voltando à aula anterior.",
          fr: "Retour à la leçon précédente.",
          de: "Zur vorherigen Lektion zurückkehren.",
          hi: "पिछले पाठ पर वापस जा रहा हूँ.",
          it: "Torno alla lezione precedente.",
          ja: "前のレッスンに戻ります。",
        });
      case "addNote":
        return studentName
          ? say(`Adding note for ${studentName}.`, {
              es: `Agregando nota para ${studentName}.`,
              pt: `Adicionando nota para ${studentName}.`,
              fr: `Ajout d'une note pour ${studentName}.`,
              de: `Notiz für ${studentName} wird hinzugefügt.`,
              hi: `मैं ${studentName} के लिए नोट जोड़ रहा हूँ.`,
              it: `Aggiungo una nota per ${studentName}.`,
              ja: `${studentName} のノートを追加します。`,
            })
          : say("Adding note.", {
              es: "Agregando nota.",
              pt: "Adicionando nota.",
              fr: "Ajout d'une note.",
              de: "Notiz wird hinzugefügt.",
              hi: "मैं नोट जोड़ रहा हूँ.",
              it: "Aggiungo una nota.",
              ja: "ノートを追加します。",
            });
      case "updateNote":
        return studentName
          ? say(`Updating note for ${studentName}.`, {
              es: `Actualizando la nota de ${studentName}.`,
              pt: `Atualizando a nota de ${studentName}.`,
              fr: `Mise à jour de la note de ${studentName}.`,
              de: `Notiz von ${studentName} wird aktualisiert.`,
              hi: `मैं ${studentName} के नोट को अपडेट कर रहा हूँ.`,
              it: `Aggiorno la nota di ${studentName}.`,
              ja: `${studentName} のノートを更新します。`,
            })
          : say("Updating note.", {
              es: "Actualizando nota.",
              pt: "Atualizando nota.",
              fr: "Mise à jour de la note.",
              de: "Notiz wird aktualisiert.",
              hi: "मैं नोट अपडेट कर रहा हूँ.",
              it: "Aggiorno la nota.",
              ja: "ノートを更新します。",
            });
      case "clearNote":
        return studentName
          ? say(`Clearing note for ${studentName}.`, {
              es: `Borrando la nota de ${studentName}.`,
              pt: `Limpando a nota de ${studentName}.`,
              fr: `Suppression de la note de ${studentName}.`,
              de: `Notiz von ${studentName} wird gelöscht.`,
              hi: `मैं ${studentName} का नोट साफ कर रहा हूँ.`,
              it: `Cancello la nota di ${studentName}.`,
              ja: `${studentName} のノートを削除します。`,
            })
          : say("Clearing note.", {
              es: "Borrando nota.",
              pt: "Limpando a nota.",
              fr: "Suppression de la note.",
              de: "Notiz wird gelöscht.",
              hi: "मैं नोट साफ कर रहा हूँ.",
              it: "Cancello la nota.",
              ja: "ノートを削除します。",
            });
      case "multipleStudents":
      case "multipleCommands":
        return say("Applying the requested changes.", {
          es: "Aplicando los cambios solicitados.",
          pt: "Aplicando as alterações solicitadas.",
          fr: "Application des modifications demandées.",
          de: "Geforderte Änderungen werden angewendet.",
          hi: "अनुरोधित बदलाव लागू कर रहा हूँ.",
          it: "Applico le modifiche richieste.",
          ja: "要求された変更を適用します。",
        });
      case "undo":
        return say("Okay, undoing the last action.", {
          es: "De acuerdo, deshaciendo la última acción.",
          pt: "Certo, desfazendo a última ação.",
          fr: "D'accord, annulation de la dernière action.",
          de: "Okay, mache die letzte Aktion rückgängig.",
          hi: "ठीक है, अंतिम क्रिया को पूर्ववत कर रहा हूँ.",
          it: "Ok, annullo l'ultima azione.",
          ja: "わかりました、直前の操作を元に戻します。",
        });
      case "redo":
        return say("Okay, reapplying the last undone action.", {
          es: "De acuerdo, rehaciendo la última acción.",
          pt: "Certo, refazendo a última ação desfeita.",
          fr: "D'accord, réapplication de la dernière action annulée.",
          de: "Okay, wende die zuletzt rückgängig gemachte Aktion erneut an.",
          hi: "ठीक है, अंतिम पूर्ववत क्रिया को फिर से लागू कर रहा हूँ.",
          it: "Ok, rieseguo l'ultima azione annullata.",
          ja: "わかりました、直前に元に戻した操作をやり直します。",
        });
      case "bringElement":
        return elementCode
          ? say(`Bringing element ${elementCode}.`, {
              es: `Trayendo el elemento ${elementCode}.`,
              pt: `Trazendo o elemento ${elementCode}.`,
              fr: `Ajout de l'élément ${elementCode}.`,
              de: `Element ${elementCode} wird hinzugefügt.`,
              hi: `तत्व ${elementCode} ला रहा हूँ.`,
              it: `Porto l'elemento ${elementCode}.`,
              ja: `要素 ${elementCode} を追加します。`,
            })
          : say("Bringing the requested element.", {
              es: "Trayendo el elemento solicitado.",
              pt: "Trazendo o elemento solicitado.",
              fr: "Ajout de l'élément demandé.",
              de: "Angefordertes Element wird hinzugefügt.",
              hi: "अनुरोधित तत्व ला रहा हूँ.",
              it: "Porto l'elemento richiesto.",
              ja: "要求された要素を追加します。",
            });
      case "clean":
        return say("Input cleared. Ready for your next command.", {
          es: "Entrada borrada. Listo para tu próximo comando.",
          pt: "Entrada limpa. Pronto para seu próximo comando.",
          fr: "Entrée effacée. Prêt pour votre prochaine commande.",
          de: "Eingabe gelöscht. Bereit für Ihren nächsten Befehl.",
          hi: "इनपुट साफ कर दिया। अगले कमांड के लिए तैयार हूँ.",
          it: "Input cancellato. Pronto per il prossimo comando.",
          ja: "入力をクリアしました。次のコマンドの準備ができています。",
        });
      default:
        if (/^opened library/i.test(msg)) {
          return msg;
        }
        if (/^closed library/i.test(msg)) {
          return msg;
        }
        if (/^inserted image:/i.test(msg)) {
          const q = msg.replace(/^[^:]+:\s*/i, "");
          return say(`Inserting image ${q}.`, {
            es: `Insertando imagen ${q}.`,
            pt: `Inserindo imagem ${q}.`,
            fr: `Insertion de l’image ${q}.`,
            de: `Bild ${q} wird eingefügt.`,
            hi: `छवि ${q} जोड़ रहा हूँ.`,
            it: `Inserisco l'immagine ${q}.`,
            ja: `画像 ${q} を挿入します。`,
          });
        }
        if (/^inserted video:/i.test(msg)) {
          const q = msg.replace(/^[^:]+:\s*/i, "");
          return say(`Inserting video ${q}.`, {
            es: `Insertando video ${q}.`,
            pt: `Inserindo vídeo ${q}.`,
            fr: `Insertion de la vidéo ${q}.`,
            de: `Video ${q} wird eingefügt.`,
            hi: `वीडियो ${q} जोड़ रहा हूँ.`,
            it: `Inserisco il video ${q}.`,
            ja: `動画 ${q} を挿入します。`,
          });
        }
        if (/^inserted audio:/i.test(msg)) {
          const q = msg.replace(/^[^:]+:\s*/i, "");
          return say(`Inserting audio ${q}.`, {
            es: `Insertando audio ${q}.`,
            pt: `Inserindo áudio ${q}.`,
            fr: `Insertion de l’audio ${q}.`,
            de: `Audio ${q} wird eingefügt.`,
            hi: `ऑडियो ${q} जोड़ रहा हूँ.`,
            it: `Inserisco l'audio ${q}.`,
            ja: `音声 ${q} を挿入します。`,
          });
        }
        if (/whiteboard:\s*starting\s*free\s*draw/i.test(lcMsg)) {
        return say("Starting free draw.", {
          es: "Iniciando dibujo libre.",
          pt: "Iniciando desenho livre.",
          fr: "Démarrage du dessin libre.",
          de: "Freies Zeichnen wird gestartet.",
          hi: "फ्री ड्रॉ शुरू कर रहा हूँ.",
          it: "Avvio del disegno libero.",
          ja: "フリードローを開始します。",
        });
      }
      if (/whiteboard:\s*drew\s*\d+\s*shapes?/i.test(lcMsg)) {
          return say("Drawing shapes.", {
            es: "Dibujando figuras.",
            pt: "Desenhando formas.",
            fr: "Dessiner des formes.",
            de: "Formen werden gezeichnet.",
            hi: "आकृतियाँ बना रहा हूँ.",
            it: "Disegnando forme.",
            ja: "図形を描いています。",
          });
      }
      if (/drawframe/i.test(lcMsg)) {
          return say("Creating a frame.", {
            es: "Creando un marco.",
            pt: "Criando uma moldura.",
            fr: "Création d'un cadre.",
            de: "Erstelle einen Rahmen.",
            hi: "फ्रेम बना रहा हूँ.",
            it: "Creazione di una cornice.",
            ja: "フレームを作成しています。",
          });
      }
      if (/applied template/i.test(lcMsg)) {
        const q = msg.replace(/^[^:]+:\s*/i, "");
          return say(`Applying template ${q}.`, {
            es: `Aplicando la plantilla ${q}.`,
            pt: `Aplicando o modelo ${q}.`,
            fr: `Application du modèle ${q}.`,
            de: `Vorlage ${q} wird angewendet.`,
            hi: `टेम्पलेट ${q} लागू कर रहा हूँ.`,
            it: `Applicazione del template ${q}.`,
            ja: `テンプレート ${q} を適用します。`,
          });
      }
      if (/template not found/i.test(lcMsg)) {
          return say("Template not found.", {
            es: "Plantilla no encontrada.",
            pt: "Modelo não encontrado.",
            fr: "Modèle introuvable.",
            de: "Vorlage nicht gefunden.",
            hi: "टेम्पलेट नहीं मिला.",
            it: "Template non trovato.",
            ja: "テンプレートが見つかりません。",
          });
      }
      if (/exporting selected collection/i.test(lcMsg)) {
          return say("Exporting selected collection.", {
            es: "Exportando la colección seleccionada.",
            pt: "Exportando a coleção selecionada.",
            fr: "Exportation de la collection sélectionnée.",
            de: "Ausgewählte Sammlung wird exportiert.",
            hi: "चयनित संग्रह निर्यात कर रहा हूँ.",
            it: "Esportazione della collezione selezionata.",
            ja: "選択したコレクションをエクスポートしています。",
          });
      }
      if (/added .* from collection to canvas/i.test(lcMsg)) {
          return say("Adding from collection to canvas.", {
            es: "Agregando desde la colección al lienzo.",
            pt: "Adicionando da coleção à tela.",
            fr: "Ajout depuis la collection vers la toile.",
            de: "Aus der Sammlung zur Leinwand hinzufügen.",
            hi: "कलेक्शन से कैनवास में जोड़ रहा हूँ.",
            it: "Aggiunta dalla collezione alla tela.",
            ja: "コレクションからキャンバスに追加しています。",
          });
      }
      if (saidDraw) {
          if (hasRect && !hasEllipse && !hasLine && !hasArrow)
            return say("Drawing a rectangle.", {
              es: "Dibujando un rectángulo.",
              pt: "Desenhando um retângulo.",
              fr: "Dessiner un rectangle.",
              de: "Ein Rechteck wird gezeichnet.",
              hi: "आयत बना रहा हूँ.",
              it: "Disegnando un rettangolo.",
              ja: "長方形を描いています。",
            });
          if (hasEllipse && !hasRect && !hasLine && !hasArrow)
            return say("Drawing a circle.", {
              es: "Dibujando un círculo.",
              pt: "Desenhando um círculo.",
              fr: "Dessiner un cercle.",
              de: "Einen Kreis zeichnen.",
              hi: "वृत्त बना रहा हूँ.",
              it: "Disegnando un cerchio.",
              ja: "円を描いています。",
            });
          if (hasLine && !hasRect && !hasEllipse && !hasArrow)
            return say("Drawing a line.", {
              es: "Dibujando una línea.",
              pt: "Desenhando uma linha.",
              fr: "Dessiner une ligne.",
              de: "Eine Linie zeichnen.",
              hi: "रेखा बना रहा हूँ.",
              it: "Disegnando una linea.",
              ja: "線を描いています。",
            });
          if (hasArrow && !hasRect && !hasEllipse && !hasLine)
            return say("Drawing an arrow.", {
              es: "Dibujando una flecha.",
              pt: "Desenhando uma seta.",
              fr: "Dessiner une flèche.",
              de: "Einen Pfeil zeichnen.",
              hi: "तीर बना रहा हूँ.",
              it: "Disegnando una freccia.",
              ja: "矢印を描いています。",
            });
          if (hasText)
            return say("Adding text.", {
              es: "Agregando texto.",
              pt: "Adicionando texto.",
              fr: "Ajout de texte.",
              de: "Text wird hinzugefügt.",
              hi: "पाठ जोड़ रहा हूँ.",
              it: "Aggiunta di testo.",
              ja: "テキストを追加しています。",
            });
          if (hasFormula)
            return say("Adding formula.", {
              es: "Agregando fórmula.",
              pt: "Adicionando fórmula.",
              fr: "Ajout de formule.",
              de: "Formel wird hinzugefügt.",
              hi: "सूत्र जोड़ रहा हूँ.",
              it: "Aggiunta di formula.",
              ja: "数式を追加しています。",
            });
          return say("Drawing.", {
            es: "Dibujando.",
            pt: "Desenhando.",
            fr: "Je dessine.",
            de: "Ich zeichne.",
            hi: "ड्रॉ कर रहा हूँ.",
            it: "Sto disegnando.",
            ja: "描いています。",
          });
      }
      if (/inserted on canvas/i.test(lcMsg)) {
          if (hasText)
            return say("Adding text.", {
              es: "Agregando texto.",
              pt: "Adicionando texto.",
              fr: "Ajout de texte.",
              de: "Text wird hinzugefügt.",
              hi: "पाठ जोड़ रहा हूँ.",
              it: "Aggiunta di testo.",
              ja: "テキストを追加しています。",
            });
          if (hasFormula)
            return say("Adding formula.", {
              es: "Agregando fórmula.",
              pt: "Adicionando fórmula.",
              fr: "Ajout de formule.",
              de: "Formel wird hinzugefügt.",
              hi: "सूत्र जोड़ रहा हूँ.",
              it: "Aggiunta di formula.",
              ja: "数式を追加しています。",
            });
          return say("Adding to canvas.", {
            es: "Agregando al lienzo.",
            pt: "Adicionando à tela.",
            fr: "Ajout à la toile.",
            de: "Zur Leinwand hinzufügen.",
            hi: "कैनवास पर जोड़ रहा हूँ.",
            it: "Aggiunta alla tela.",
            ja: "キャンバスに追加しています。",
          });
      }
      return execResult?.message ||
        say("Done.", {
          es: "Hecho.",
          pt: "Concluído.",
          fr: "Terminé.",
          de: "Fertig.",
          hi: "हो गया.",
          it: "Fatto.",
          ja: "完了しました。",
        });
    }
  };

  return { speakReply, buildVoiceReply };
};